import { useEffect, useMemo, useRef, useState } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'

import type { CMFormSection } from './ContentModuleFormScreen'
import type { FAQSectionRef } from './FAQSection'
import { useStepResources } from './useStepResources'

type DescriptionAIState = 'idle' | 'loading' | 'pending'
export type ContentAccessType = 'full_access' | 'information_only' | 'media_only'

export function useReviewModalState(props: CMFormSection) {
  const { api } = useGlobalState()

  const [pendingFileName, setPendingFileName] = useState<string | null>(null)
  const [contentAccessType, setContentAccessType] = useState<ContentAccessType>('information_only')
  const [pendingDescription, setPendingDescription] = useState<string | null>(null)

  const [descriptionAIState, setDescriptionAIState] = useState<DescriptionAIState>('idle')
  const [descriptionSuggestion, setDescriptionSuggestion] = useState<string | null>(null)

  const autoGenerationTriggeredRef = useRef<string | null>(null)
  const descriptionAutoGenTriggeredRef = useRef<string | null>(null)
  const accessTypeInitRef = useRef<string | null>(null)
  const faqSectionRef = useRef<FAQSectionRef>(null)

  const { stepResources, uploadedFileResource, sourceFileResource } = useStepResources(
    props.stepId,
    props.stepContentQuery.data?.sourceUrl,
  )

  const hasContent = !!props.stepContentQuery.data
  const hasExtractedContent = !!props.stepContentQuery.data?.content?.trim()

  const showInApp = contentAccessType === 'full_access' || contentAccessType === 'media_only'
  const useInRag = contentAccessType === 'full_access' || contentAccessType === 'information_only'

  const existingSupplementalResources = useMemo(() => {
    const step = props.stepsQuery.data?.find(s => s.stepId === props.stepId)
    return step?.resources ?? []
  }, [props.stepsQuery.data, props.stepId])

  // Seed file description suggestion from task_steps descriptionAi when description is blank
  useEffect(() => {
    if (!props.stepId || !props.stepsQuery?.data) return
    if (stepResources === undefined) return

    const step = props.stepsQuery.data.find(s => s.stepId === props.stepId)
    if (!step?.descriptionAi?.trim()) return

    const descriptionBlank = !(pendingDescription?.trim() || sourceFileResource?.description?.trim())
    if (!descriptionBlank) return
    if (descriptionAIState !== 'idle') return

    setDescriptionSuggestion(step.descriptionAi.trim())
    setDescriptionAIState('pending')
  }, [
    props.stepId,
    props.stepsQuery?.data,
    pendingDescription,
    sourceFileResource?.description,
    descriptionAIState,
    stepResources,
  ])

  // Initialize description from source file resource
  useEffect(() => {
    if (sourceFileResource) {
      setPendingDescription(sourceFileResource.description ?? '')
    }
  }, [sourceFileResource?.resourceId, sourceFileResource?.description])

  // Initialize contentAccessType from source file resource + step data (once per resource)
  useEffect(() => {
    if (!sourceFileResource) return

    const resourceKey = `${sourceFileResource.resourceId}-${sourceFileResource.showInApp}-${sourceFileResource.showInAppSupported}`
    if (accessTypeInitRef.current === resourceKey) return
    accessTypeInitRef.current = resourceKey

    if (!sourceFileResource.showInAppSupported) {
      setContentAccessType('information_only')
      return
    }

    const stepData = props.stepsQuery.data?.find(s => s.stepId === props.stepId)
    const stepUseInRag = stepData?.useInRag ?? true
    const resourceShowInApp = sourceFileResource.showInApp ?? false

    if (resourceShowInApp && stepUseInRag) {
      setContentAccessType('full_access')
    } else if (resourceShowInApp && !stepUseInRag) {
      setContentAccessType('media_only')
    } else {
      setContentAccessType('information_only')
    }
  }, [
    sourceFileResource?.resourceId,
    sourceFileResource?.showInApp,
    sourceFileResource?.showInAppSupported,
    props.stepsQuery.data,
    props.stepId,
  ])

  async function generateSourceFileDescription() {
    if (!props.stepId) return

    setDescriptionAIState('loading')

    try {
      const response = await api.safeRequest(api.generate, {
        step_id: props.stepId,
        system_prompt_template_id: 'generate_cm_file_description',
        message_template_id: 'generate_step_message',
        extra_data: {
          accepted_data: pendingDescription || undefined,
        },
      })

      if (response?.ok && response.data) {
        const suggestion = (response.data as string).trim().replace(/\n\s*\n/g, '\n\n')
        setDescriptionSuggestion(suggestion)
        setDescriptionAIState('pending')
      } else {
        setDescriptionAIState('idle')
      }
    } catch {
      setDescriptionAIState('idle')
    }
  }

  async function generateSourceFileDescriptionWithGuide(instructions: string) {
    if (!props.stepId) return

    setDescriptionAIState('loading')

    try {
      const extraData: Record<string, string | undefined> = {
        accepted_data: pendingDescription || undefined,
        user_instructions: instructions.trim() || undefined,
      }
      if (descriptionAIState === 'pending' && descriptionSuggestion) {
        extraData.proposed_data = descriptionSuggestion
      }

      const response = await api.safeRequest(api.generate, {
        step_id: props.stepId,
        system_prompt_template_id: 'generate_cm_file_description',
        message_template_id: 'generate_step_message',
        extra_data: extraData,
      })

      if (response?.ok && response.data) {
        const suggestion = (response.data as string).trim().replace(/\n\s*\n/g, '\n\n')
        setDescriptionSuggestion(suggestion)
        setDescriptionAIState('pending')
      } else {
        setDescriptionAIState('idle')
      }
    } catch {
      setDescriptionAIState('idle')
    }
  }

  function acceptDescriptionSuggestion() {
    if (descriptionSuggestion) {
      setPendingDescription(descriptionSuggestion)
      setDescriptionSuggestion(null)
      setDescriptionAIState('idle')
    }
  }

  function rejectDescriptionSuggestion() {
    setDescriptionSuggestion(null)
    setDescriptionAIState('idle')
  }

  const isMutating = props.stepUpdateMutation.isPending || props.resourceUpdateMutation.isPending

  return {
    stepResources,

    pendingFileName,
    setPendingFileName,
    contentAccessType,
    setContentAccessType,
    showInApp,
    useInRag,
    pendingDescription,
    setPendingDescription,

    descriptionAIState,
    descriptionSuggestion,
    generateSourceFileDescription,
    generateSourceFileDescriptionWithGuide,
    acceptDescriptionSuggestion,
    rejectDescriptionSuggestion,

    autoGenerationTriggeredRef,
    descriptionAutoGenTriggeredRef,
    faqSectionRef,

    hasContent,
    hasExtractedContent,
    isMutating,
    uploadedFileResource,
    sourceFileResource,
    existingSupplementalResources,
  }
}
